import { randomId } from '@aha-app/mvc';
import * as Sentry from '@sentry/browser';
import Debug from 'debug';
import Serialization from '../collaboration/Serialization';
import { type JsonData } from '../components/Assistant/XMLWithEmbeddedJSONParser';
import type WhiteboardController from '../controllers/WhiteboardController';
import ToolDelete from '../tools/ToolDelete';
import ToolSelectAll from '../tools/ToolSelectAll';
import { nullHrefsInJsonHTML } from '../utils/jsonLinkSanitizer';
import { sanitizeShapeData, validateAIResponse } from '../utils/jsonValidator';
import { convertXmlToWhiteboardObjects } from '../utils/wireframe_ai/xmlAssistantParser';
import {
  isFrameLike,
  hasEmbeddedTextBehaviors,
  hasFrameMemberBehaviors,
  isShape,
  isSimpleGroup,
  isTable,
  hasConnectorAttachmentBehaviours,
  isConnector,
} from '../objects/guards';
import {
  isSerializedKey,
  type IDataStoreRecordBehaviors,
} from '../collaboration/DataStoreRecordBehaviors';
import { getMaxZ, Z_INCREMENT } from '../utils/zManager';
import { AttachmentPoint, ConnectorEnd } from '../types';
import type Connector from '../objects/Connector';
import { isEnabledFrame } from '../objects/FrameBehaviors';
import { endStyleToString } from '../utils/connectors';
import { type CONNECTOR_END_STYLE } from '../constants';
import { bringObjectIntoView, objectIsInView } from '../utils/zoom';
import { type Rect } from '../types';
import { type IPoint } from 'fabric/fabric-impl';
import { pointsToRect } from '../utils/geometry';
import { normalizeVerticalAlign } from '../utils/verticalAlign';

const debug = Debug('whiteboard:assistant');

export interface WhiteboardOperation {
  type: 'create' | 'update' | 'delete' | 'select' | 'record';
  data: JsonData;
}

const stripEmptyValues = <T extends {}>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== null && value !== undefined
    )
  ) as T;

export interface WhiteboardObjectSummary {
  id: string;
  type: string;
  shapeName?: string;
  textContent?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  angle?: number | null;
  fill?: string | null;
  stroke?: string | null;
  referenceNum?: string;
  endStyles?: (keyof typeof CONNECTOR_END_STYLE)[];
}

export const getWhiteboardContent = (
  objects: fabric.Object[]
): WhiteboardObjectSummary[] => {
  return objects.flatMap(obj => {
    if (isSimpleGroup(obj)) {
      return getWhiteboardContent(obj.getObjects());
    }

    let referenceNum: string | undefined;
    try {
      // @ts-expect-error this is a hack due to missing types/guards
      referenceNum = obj.recordInfo?.attributes?.referenceNum;
    } catch {}

    // TODO consider sending plain text here?
    const textProperties = hasEmbeddedTextBehaviors(obj)
      ? {
          textContent: obj.textContent,
          textAlign: obj.textAlign,
          verticalAlign: normalizeVerticalAlign(obj.verticalAlign),
        }
      : undefined;

    const content: WhiteboardObjectSummary = {
      id: obj.id,
      type: obj.type,
      ...(isShape(obj) ? { shapeName: obj.shapeName } : {}),
      ...textProperties,
      left: Math.round(obj.left),
      top: Math.round(obj.top),

      // for most shapes, we combine these for simplicity, because it makes no difference to
      // rendering if you control size by scale or raw pixels
      // NOTE scale matters for wireframes, so we'll need to handle them differently here when we support editing them
      width: Math.round(obj.width * (obj.scaleX ?? 1)),
      height: Math.round(obj.height * (obj.scaleY ?? 1)),

      angle: obj.angle || null, // don't send 0 as that's the default
      // opacity: obj.opacity,
      // strokeWidth: obj.strokeWidth,
      fill: obj.fill,
      stroke: obj.stroke,
      referenceNum,
    };

    // include special table content properties
    if (isTable(obj)) {
      for (const [key, value] of Object.entries(obj.toJSON())) {
        if (
          key.startsWith('col_') ||
          key.startsWith('row_') ||
          key.startsWith('cell_') ||
          key.startsWith('merged_') ||
          [
            'numColumns',
            'numRows',
            'textFields',
            'hasHeaderRow',
            'hasHeaderColumn',
          ].includes(key)
        ) {
          content[key] = value;
        }
      }
    }

    // Translate connector end styles back to strings, as we only tell the model
    // the string values of endStyles in the whiteboard prompt schema
    if (isConnector(obj)) {
      if (obj.endStyles) {
        const endStyles = obj.endStyles.map((style, i) => {
          const endStyle = endStyleToString(style);
          if (!endStyle) {
            return i === 0 ? 'NONE' : 'FILLED_ARROW';
          }
          return endStyle;
        });
        content.endStyles = endStyles;
      }
    }

    return stripEmptyValues(content);
  });
};

export class IdMapper {
  idMap: Record<string, string>;

  constructor() {
    this.idMap = {};
  }

  replaceId(object: { id: string }) {
    const oldId = object.id;
    object.id = randomId();
    this.idMap[oldId] = object.id;
  }

  updateId(object: { id: string }) {
    if (this.idMap[object.id]) {
      object.id = this.idMap[object.id];
    }
  }

  idFor(oldId: unknown) {
    return this.idMap[String(oldId)] || oldId;
  }

  containsNewId(id: string) {
    return Object.values(this.idMap).includes(id);
  }
}

type PanTarget = Partial<Rect> & { points?: IPoint[] };
const normalisePanTarget = (obj: PanTarget): Rect | undefined => {
  if (obj.top !== undefined && obj.left !== undefined) {
    return { width: 0, height: 0, ...obj } as Rect;
  } else if (Array.isArray(obj.points) && obj.points.length) {
    return pointsToRect(obj.points);
  }
};

class AssistantService {
  currentEditGroup: string | null = null;
  newIdMap: IdMapper;

  constructor(private readonly whiteboardController: WhiteboardController) {
    this.newIdMap = new IdMapper();
  }

  // TODO: consider pausing auto-pan if the user pans manually
  // perhaps a timeout after the last pan, before we resume panning to edits?
  private currentPromise: Promise<void> | null = null;
  private newObjects: Rect[] = [];

  /** Called whenever we have a new object to pan to, makes a best effort at waiting for existing pans and
   * subsequent updates before panning to the new objects. The idea is that panning happens before
   * the update, so the user sees it happen */
  private async panToEdits(
    editGroup: string,
    newObject?: PanTarget
  ): Promise<void> {
    if (editGroup !== this.currentEditGroup) {
      // reset when we start a new edit group
      this.newObjects = [];
      this.currentEditGroup = editGroup;
    }
    if (newObject) {
      const obj = normalisePanTarget(newObject);
      if (obj) {
        this.newObjects.push(obj);
      }
    }

    // always wait for any existing pan in progress, there is a high chance once it is
    // done, we won't need to pan further for subsequent edits
    if (this.currentPromise) {
      await this.currentPromise;
    }

    // if the new object is already visible, we can skip panning and just process it
    if (objectIsInView(this.whiteboardController, this.newObjects)) {
      return;
    }

    // if another parallel call happened while we were waiting, we don't need to do anything
    // because it will have picked up any new objects already
    if (this.currentPromise) {
      return await this.currentPromise;
    }

    // bring any newly created objects into view
    const promise = bringObjectIntoView(
      this.whiteboardController,
      this.newObjects
    );
    this.newObjects = [];

    // set currentPromise for the duration of the pan, so any other calls will wait for it
    this.currentPromise = promise;
    await promise;
    this.currentPromise = null;

    // wait a tiny bit so the user sees the result
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  async handleStreamChunk(
    { type, data }: WhiteboardOperation,
    groupId: string
  ): Promise<boolean> {
    debug(
      `Received change ${type} with value ${JSON.stringify({ type, data })}`
    );

    switch (type) {
      case 'create':
      case 'update': {
        // sometimes it provides an array within the tag, so we just deal with that possibility
        const ops = (Array.isArray(data) ? data : [data]) as JsonData[];
        const results = await Promise.all(
          type === 'create'
            ? ops.map(async op => await this.handleCreate(op, groupId))
            : ops.map(async op => await this.handleUpdate(op, groupId))
        );

        return results.every(Boolean);
      }

      case 'select': {
        if (!Array.isArray(data)) {
          console.error('Select value is not an array', data);
          return false;
        }

        const selectValues = data.map(id => this.newIdMap.idFor(id));
        this.handleSelection(selectValues);

        return true;
      }
      case 'delete': {
        if (!Array.isArray(data)) {
          console.error('Delete value is not an array', data);
          return false;
        }

        const deleteValues = data.map(id => this.newIdMap.idFor(id));
        this.handleDeletes(deleteValues);

        return true;
      }
      case 'record': {
        return this.handleRecord(data);
      }
      default: {
        console.error('Unknown change type', { type, data });
        return false;
      }
    }
  }

  async handleUpdate(change: JsonData, groupId: string): Promise<boolean> {
    if (Array.isArray(change)) {
      return false;
    }
    nullHrefsInJsonHTML(change);

    const { id: incomingId, ...changedValues } = change;

    const id = this.newIdMap.idFor(incomingId) || incomingId;

    const object = this.whiteboardController.dataStoreObjects.findRecordById(
      id as RecordId
    );

    if (!object) {
      debug(
        `Object ${id} not found in data store for assistant change ${JSON.stringify(
          change
        )}.`
      );
      return false;
    }

    await this.panToEdits(groupId, object as PanTarget);

    const validatedObject = this.validateUpdate(object, changedValues);

    if (!validatedObject) {
      console.error('Invalid update chunk', changedValues);
      return false;
    }

    // It would be very weird for the response to create an object and then
    // update it in the same response, but it could happen.
    this.newIdMap.updateId(object);

    this.postProcessing('update', validatedObject, object);

    this.whiteboardController.canvas.requestRenderAll();

    return true;
  }

  private validateUpdate(
    fabricObject: fabric.Object & IDataStoreRecordBehaviors,
    changedValues: Record<string, unknown>
  ) {
    // the agent assumes scaleY/scaleX are 1, so we need to adjust if that's not true
    if (
      changedValues.width &&
      typeof changedValues.width === 'number' &&
      !changedValues.scaleX
    ) {
      changedValues.width /= fabricObject.scaleX ?? 1;
    }
    if (
      changedValues.height &&
      typeof changedValues.height === 'number' &&
      !changedValues.scaleY
    ) {
      changedValues.height /= fabricObject.scaleY ?? 1;
    }

    const { textFields, ...otherChanges } = changedValues;

    const updated = {
      ...fabricObject.toJSON(),
      ...otherChanges,
    };

    // if there are textFields, we need to merge, because the LLM may make partial updates
    if (textFields && isTable(fabricObject)) {
      updated.textFields ??= {};

      for (const [key, value] of Object.entries(textFields)) {
        updated.textFields[key] ??= {};
        updated.textFields[key].textContent ??= '';
        updated.textFields[key] = {
          ...updated.textFields[key],
          ...value,
        };
      }
    }

    // TODO updates don't always render, esp table textField textContent changes

    const validatedJsonResponse = validateAIResponse(stripEmptyValues(updated));

    if (!validatedJsonResponse.success) {
      // To test this just ask 'update the circle with an invalid color'
      console.error(
        'Failed to validate JSON update response',
        validatedJsonResponse,
        validatedJsonResponse.errors.flatten()
      );
      Sentry.captureMessage('AI update response failed validation', {
        contexts: {
          aiData: {
            changedValues: JSON.stringify(changedValues),
            errors: JSON.stringify(validatedJsonResponse.errors),
          },
        },
      });
      return false;
    }

    if (validatedJsonResponse.data) {
      return validatedJsonResponse.data;
    } else {
      console.error(
        'Validation response did not have data',
        validatedJsonResponse
      );
      return false;
    }
  }

  private validateCreate(responseValue: Record<string, any>) {
    debug('Validating create object', responseValue);

    const validatedJsonResponse = validateAIResponse(responseValue);

    if (!validatedJsonResponse.success) {
      console.error(
        'Failed to validate JSON create response',
        validatedJsonResponse,
        validatedJsonResponse.errors.flatten()
      );
      // To test this just ask 'create a circle with an invalid color'
      Sentry.captureMessage('AI create response failed validation', {
        contexts: {
          aiData: {
            responseValue: JSON.stringify(responseValue),
            errors: JSON.stringify(validatedJsonResponse.errors),
          },
        },
      });
      return null;
    } else {
      debug('Response passed validation');

      if (validatedJsonResponse.data) {
        return validatedJsonResponse.data;
      } else {
        console.error('No data in validated response', validatedJsonResponse);
        return null;
      }
    }
  }

  async handleCreate(llmObject: JsonData, groupId: string): Promise<boolean> {
    let top = 0;

    const parsed = sanitizeShapeData(llmObject);

    if (!parsed) {
      return false;
    }

    const newObject = {
      ...this.defaultsFor(parsed),
      ...parsed,
    };

    if (!newObject.id) {
      newObject.id = randomId();
    }

    nullHrefsInJsonHTML(newObject);

    // This is only a fallback if the AI does not pass us a value
    if (!newObject.top) {
      newObject.top = top;
      top += 70;
    }

    if (!newObject.z) {
      newObject.z =
        getMaxZ(this.whiteboardController.canvas.getObjects()) + Z_INCREMENT;
    }

    const validatedNewObject = this.validateCreate(newObject);

    if (!validatedNewObject) {
      return false;
    }

    this.newIdMap.replaceId(validatedNewObject as { id: string });

    await this.panToEdits(groupId, validatedNewObject as PanTarget);

    // If the object has a container, replace the container id with the new generated id of the parent container
    if (typeof validatedNewObject.container === 'string') {
      debug('Replacing container ids');
      const newContainerId = this.newIdMap.idFor(validatedNewObject.container);
      if (newContainerId) {
        validatedNewObject.container = String(newContainerId);
      }
    }

    // If creating a group object, we need to replace the ids of the objects in the group
    if (
      'objects' in validatedNewObject &&
      Array.isArray(validatedNewObject.objects)
    ) {
      debug('Replacing group object ids');
      validatedNewObject.objects = validatedNewObject.objects.map(objectId =>
        this.newIdMap.idFor(objectId)
      );
    }

    // If creating a label, map connectorId from LLM to actual conntector id in
    // whiteboard
    if (
      'connectorId' in validatedNewObject &&
      typeof validatedNewObject.connectorId === 'string'
    ) {
      debug('Replacing connectorId');
      validatedNewObject.connectorId = this.newIdMap.idFor(
        validatedNewObject.connectorId
      );
    }

    const newDoc = { objects: [validatedNewObject] };

    Serialization.restoreDocument(
      this.whiteboardController,
      newDoc,
      // Pass in all the current ids on the document
      this.whiteboardController.canvas.getObjects().map(object => object.id)
    );

    // We only ever create one object at a time
    const object = this.whiteboardController.dataStoreObjects.findRecordById(
      validatedNewObject.id as RecordId
    );

    this.postProcessing('create', validatedNewObject, object);

    return true;
  }

  defaultsFor(
    llmObject:
      | { type: 'shape'; shapeName: string }
      | { type: 'wireframe'; wireframeName: string }
      | { type: 'device'; deviceName: string }
      | { type: string; [key: string]: any }
  ) {
    switch (llmObject.type) {
      case 'shape':
        return this.whiteboardController.defaultsManager.for(
          'shape',
          llmObject.shapeName
        );

      case 'wireframe':
        return this.whiteboardController.defaultsManager.for(
          'wireframe',
          llmObject.wireframeName
        );

      case 'device':
        return this.whiteboardController.defaultsManager.for(
          'device',
          llmObject.deviceName
        );

      case 'path':
        return {};

      default:
        return this.whiteboardController.defaultsManager.for(llmObject.type);
    }
  }

  handleSelection(selection) {
    const dataStore = this.whiteboardController.dataStoreObjects;

    const objects = selection
      .map(id => dataStore.findRecordById(id))
      .filter(object => object);
    if (objects.length > 0) {
      ToolSelectAll.setSelection(this.whiteboardController.canvas, objects);
    }
  }

  handleDeletes(deletes) {
    const dataStore = this.whiteboardController.dataStoreObjects;

    const objects = deletes
      .map(id => dataStore.findRecordById(id))
      .filter(object => object);
    if (objects.length > 0) {
      ToolDelete.deleteObjects(objects, this.whiteboardController);
    }
  }

  handleRecord(data: JsonData): boolean {
    const { record_url: url, left, top } = data as any;
    const objects = this.whiteboardController.actionPlaceAhaRecord(
      url,
      left,
      top
    );

    // add the record ref to the id map, because the agent might try to update by record id
    const object = objects?.[0]; // there will only be one, because we only passed one url
    const ref = url.split('/').pop();
    if (object && ref) {
      this.newIdMap.idMap[ref] = object.id;
    }

    return true;
  }

  convertXMLToWhiteboard(responseText: string) {
    try {
      const components = convertXmlToWhiteboardObjects(responseText);
      if (!components) {
        throw new Error('No components found in response');
      }

      // Process and validate components
      const validatedObjects = components
        .map(component => this.validateCreate(component))
        .filter(Boolean);

      if (!validatedObjects.length) {
        throw new Error('No components passed validation');
      }

      // Add objects to the whiteboard
      const newDoc = { objects: validatedObjects };
      const objects = this.whiteboardController.actionPlaceObjects(newDoc);

      // Configure layouts for proper rendering
      this.configureFrameLayouts(Array.isArray(objects) ? objects : [objects]);
    } catch (e) {
      this.whiteboardController.actionShowMessage(
        'There was an error in the AI response. Please modify your prompt and try again.'
      );
      console.error('Error parsing AI response', e);
    }
  }

  /**
   * Special case handling for certain types of objects, e.g. frames and connectors,
   * where we need to manually fire certain methods after a change
   */
  private postProcessing(
    op: WhiteboardOperation['type'],
    validatedObject: ReturnType<typeof validateAIResponse>['data'],
    newObject: fabric.Object
  ) {
    if (op === 'create') {
      // Always update frame containment for new objects
      this.updateContainer(newObject);
      // Handle connectors and attachments as necessary
      this.updateConnectorAttachments(validatedObject, newObject);
    } else if (op === 'update') {
      this.maybeUpdateContainers(validatedObject, newObject);
      this.updateConnectorAttachments(validatedObject, newObject);
    }
  }

  /**
   * Determines if updated objects need to have their frame updated, based on
   * several different keys that might affect frame containment
   */
  private maybeUpdateContainers(validatedObject, object) {
    const isFrame = isEnabledFrame(object);
    let moved = false;
    let resized = false;

    if (isFrame) {
      object.startMoveWithContents();
    }

    for (const [key, value] of Object.entries(validatedObject)) {
      if (isSerializedKey(object, key)) {
        // If the object has a container, replace the container id with the new generated id of the parent container
        if (key === 'container') {
          const newContainerId = this.newIdMap.idFor(value);
          if (newContainerId) {
            object.set(key, newContainerId);
          }
        } else {
          object.set(key, value);
        }

        moved ||= ['left', 'top'].includes(key);
        resized ||= ['scaleX', 'scaleY', 'width', 'height'].includes(key);
      }
    }

    if (moved || resized) {
      object.setCoords();
      this.updateContainer(object);
    }

    if (isFrame && moved) {
      object.moveWithContents();
      object.movedWithContents();
    }

    if (isFrame && resized) {
      // When a frame is resized, re-capture the contents to remove anything
      // that might now be outside the frame
      object.captureContents();
    }
  }

  /**
   * Enables layouts and sets vertical sizing for layouts to "children".
   * - Enabling layouts after all objects are added ensures that no layout will
   *   fire prematurely and mess up the design
   */
  private configureFrameLayouts(objects: fabric.Object[]) {
    const frames = objects.filter(isFrameLike);

    // Enable layouts on all frames after all content has been added
    this.whiteboardController.aroundBulkAddingCode(() => {
      frames.forEach(frame => frame.set('layoutEnabled', true));
    });
  }

  private updateContainer(object: fabric.Object) {
    // Find the topmost (highest z) frame that contains this object
    if (hasFrameMemberBehaviors(object)) {
      const newFrame = this.whiteboardController.canvas
        .getObjects()
        .filter(obj => isEnabledFrame(obj) && object.id !== obj.id)
        .reverse()
        .find(obj => obj.containsObject(object));

      if (!newFrame) {
        object.set('container', null);
      } else if (isEnabledFrame(newFrame)) {
        // avoid trigger collab ops since we will shortly be updating this value again
        object.container = null;
        newFrame.captureContents();
      }
    }
  }

  private updateConnectorAttachments(
    validatedObject: ReturnType<typeof validateAIResponse>['data'],
    newObject: fabric.Object
  ) {
    if (
      newObject?.type === 'connector' &&
      validatedObject?.type === 'connector' &&
      (validatedObject?.fromId || validatedObject?.toId)
    ) {
      this.attachConnectors(
        newObject as Connector,
        validatedObject?.fromId,
        validatedObject?.toId
      );
    }
  }

  /**
   * Handles connector attachments when creating or updating connectors
   * with fromId/toId properties. Updates attachments as neccessary then triggers
   * connector repositioning
   */
  private attachConnectors(
    connector: Connector,
    fromId?: RecordId,
    toId?: RecordId
  ) {
    const getObject = (id: RecordId) => {
      const resolvedFromId = this.newIdMap.idFor(id) || id;
      return this.whiteboardController.dataStoreObjects.findRecordById(
        resolvedFromId
      );
    };

    const attachObject = (id: RecordId, end: ConnectorEnd) => {
      const obj = getObject(id);
      if (obj && hasConnectorAttachmentBehaviours(obj)) {
        // TODO handle attachment point properly
        obj.attachConnector(connector, end, AttachmentPoint.Center);
        obj.synchronize();
      }
    };
    if (fromId) {
      attachObject(fromId, ConnectorEnd.CONNECTOR_START);
    }

    if (toId) {
      attachObject(toId, ConnectorEnd.CONNECTOR_END);
    }

    connector.constrainConnector({});
  }
}

export default AssistantService;
